<template lang="pug">
#notification-setting-edit
  .box
    .box-header
      .title
        span 通知設定
      .note
        span ご家族もしくは施設関係者が連絡や面会予約等をした際に、指定のメールアドレスに通知することが出来ます。
        br
        span 機能、グループごとの通知先メールアドレスを記入して、画面下部の更新ボタンをクリックしてください。
    .box-body.register-body
      b-tabs
        b-tab.tab(title="家族ユーザー" v-if="showFamiliesNotificationSetting")
          NotificationSettingTab(v-model="residentGroupTableItems" :fields="residentGroupTableFields" :showText.sync="messageShowText"
            :isLoading="residentGroupTableIsLoading" :errorMessage="residentGroupErrorMessage" @update="onUpdateButton(0)")
        b-tab.tab(title="施設関係者ユーザー" v-if="showOtherUserNotificationSetting")
          NotificationSettingTab(v-model="otherUserGroupTableItems" :fields="otherUserGroupTableFields" :showText.sync="otherUserMessageShowText"
            :isLoading="otherUserGroupTableIsLoading" :errorMessage="otherUserGroupErrorMessage" @update="onUpdateButton(1)")
        b-tab.tab(title="他事業所連携" v-if="showOtherOfficeNotificationSetting")
          NotificationSettingTab(v-model="otherOfficeTableItems" :fields="otherOfficeTableFields" :showText.sync="otherOfficeMessageShowText"
            :isLoading="otherOfficeTableIsLoading" :errorMessage="otherOfficeErrorMessage" @update="onUpdateButton(2)")

  OkCancelModal(v-model="showUpdateModal" title="通知設定を更新しますか？" @ok="updateNotificationSettings")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="createView")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import FacilitiesService, { FacilityGetModel, NotificationObject, Notifications } from "@/commons/services/Facilities";
import NotificationsService, { NotificationSettingsPatchModel } from "@/commons/services/Notifications";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import OtherUserGroupModel from "@/commons/models/OtherUserGroup";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import NotificationSettingTab from "@/pc/containers/settings/NotificationSettingTab.vue";
import { NotificationSettingTableField, NotificationSettingTableItem } from "@/pc/containers/settings/NotificationSettingTable.vue";
import SystemError from "@/commons/models/SystemError";
import { PlanType } from "@/commons/services/Plans";

/**
 * ### 通知設定変更コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    NotificationSettingTab
  }
})
export default class Facility extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** 家族・施設関係者のLINEbot操作の通知設定サービス */
  @Inject("NotificationsService") notificationsService!: NotificationsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** 施設 */
  facility: FacilityGetModel | null = null;
  /** 家族・施設関係者のLINEbot操作の通知設定をMAP化したもの */
  notificationMaps: NotificationMaps | null = null;

  /** 利用者グループ列名 */
  residentGroupTableFields: Array<NotificationSettingTableField> = [];
  /** 利用者グループ行の情報 */
  residentGroupTableItems: Array<NotificationSettingTableItem> = [];
  /** テーブルのローディング表示可否 */
  residentGroupTableIsLoading: boolean = true;
  /** 家族メッセージで本文を表示するか */
  messageShowText: boolean = false;
  /** 利用者グループ通知更新エラーメッセージ */
  residentGroupErrorMessage: string = "";

  /** 施設関係者グループ列名 */
  otherUserGroupTableFields: Array<NotificationSettingTableField> = [];
  /** 施設関係者グループ行の情報 */
  otherUserGroupTableItems: Array<NotificationSettingTableItem> = [];
  /** テーブルのローディング表示可否 */
  otherUserGroupTableIsLoading: boolean = true;
  /** 施設関係者メッセージで本文を表示するか */
  otherUserMessageShowText: boolean = false;
  /** 施設関係者グループ通知更新エラーメッセージ */
  otherUserGroupErrorMessage: string = "";

  /** 他事業所連携列名 */
  otherOfficeTableFields: Array<NotificationSettingTableField> = [];
  /** 他事業所連携行の情報 */
  otherOfficeTableItems: Array<NotificationSettingTableItem> = [];
  /** テーブルのローディング表示可否 */
  otherOfficeTableIsLoading: boolean = true;
  /** 他事業所連携メッセージで本文を表示するか */
  otherOfficeMessageShowText: boolean = false;
  /** 他事業所連携通知更新エラーメッセージ */
  otherOfficeErrorMessage: string = "";

  /** 通知先更新種別 */
  notificationTabType: NotificationTabType = NotificationTabType.UNKNOWN;

  get showFamiliesNotificationSetting(): boolean {
    if (userStore.facility.useFamilyService) {
      if (PlanType.FREE < userStore.facility.planType) {
        return true;
      }
    }
    return false;
  }

  get showOtherUserNotificationSetting(): boolean {
    if (PlanType.BASIC <= userStore.facility.planType) {
      return this.showFamiliesNotificationSetting && true;
    } else {
      return this.showFamiliesNotificationSetting && false;
    }
  }

  get showOtherOfficeNotificationSetting(): boolean {
    return userStore.facility.useOtherOfficeConnectionService && userStore.loginUser.permissions.functions.useOtherOfficeConnectionService;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const residentGroupTableFields: Array<NotificationSettingTableField> = this.createResidentGroupTableFields();
    this.residentGroupTableFields = residentGroupTableFields;
    const otherUserGroupTableFields: Array<NotificationSettingTableField> = this.createOtherUserGroupTableFields();
    this.otherUserGroupTableFields = otherUserGroupTableFields;
    const otherOfficeTableFields: Array<NotificationSettingTableField> = this.createOtherOfficeTableFields();
    this.otherOfficeTableFields = otherOfficeTableFields;

    // 動的に変更する画面情報を作成する
    this.residentGroupTableItems = [];
    this.otherUserGroupTableItems = [];
    this.otherOfficeTableItems = [];

    await this.createView();

    return;
  }

  /**
   * ### 利用者グループテーブルの列名を作成する
   *
   * - 機能使用可否に応じて表示対象の列は動的に切り替える
   */
  public createResidentGroupTableFields(): Array<NotificationSettingTableField> {
    const fields: Array<NotificationSettingTableField> = [];

    fields.push({ key: "groupName", label: "利用者グループ名", sortable: false, sortDirection: "desc", class: "resident-group-name" });
    fields.push({ key: "family", label: "家族ユーザー登録／登録削除", sortable: false, sortDirection: "desc", class: "family" });
    fields.push({ key: "message", label: "家族からの連絡／返信", sortable: false, sortDirection: "desc", class: "message" });
    if (userStore.facility.permittedFunctions.reservation == true || userStore.facility.permittedFunctions.onlineReservation == true) {
      fields.push({ key: "reservation", label: "面会予約申込み／キャンセル", sortable: false, sortDirection: "desc", class: "reservation" });
    }
    if (userStore.facility.permittedFunctions.outing == true) {
      fields.push({ key: "outing", label: "外出外泊届け／修正／キャンセル", sortable: false, sortDirection: "desc", class: "outing" });
    }
    if (userStore.facility.permittedFunctions.event == true) {
      fields.push({ key: "event", label: "イベント参加申込み／キャンセル", sortable: false, sortDirection: "desc", class: "event" });
    }
    if (userStore.facility.permittedFunctions.picture == true) {
      fields.push({ key: "picture", label: "写真印刷依頼", sortable: false, sortDirection: "desc", class: "picture" });
    }

    return fields;
  }

  /**
   * ### 施設関係者グループテーブルの列名を作成する
   */
  public createOtherUserGroupTableFields(): Array<NotificationSettingTableField> {
    const fields: Array<NotificationSettingTableField> = [];

    fields.push({ key: "groupName", label: "施設関係者グループ名", sortable: false, sortDirection: "desc", class: "other-user-group-name" });
    fields.push({ key: "otherUser", label: "施設関係者登録／登録削除", sortable: false, sortDirection: "desc", class: "other-user" });
    fields.push({
      key: "otherUserMessage",
      label: "施設関係者からの連絡／返信",
      sortable: false,
      sortDirection: "desc",
      class: "other-user-message"
    });

    return fields;
  }

  public createOtherOfficeTableFields(): Array<NotificationSettingTableField> {
    const fields: Array<NotificationSettingTableField> = [];
    fields.push({ key: "otherOfficeMessage", label: "他事業所からのメッセージ", sortable: false, sortDirection: "desc", class: "other-office-message" });
    fields.push({ key: "otherOfficeOnlineMeeting", label: "他事業所からのオンライン会議案内", sortable: false, sortDirection: "desc", class: "other-office-online-meeting" });
    return fields;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 最新の施設情報を取得する
   *   - 施設情報から更新に必要な通知設定、更新日時を取得する
   *   - 利用者グループを取得する
   * - 施設関係者グループ種類と種類名の変換用にグループ種類情報のMAPを作る
   * - 施設関係者グループを取得する
   */
  public async createView(): Promise<void> {
    this.residentGroupTableIsLoading = true;
    this.otherUserGroupTableIsLoading = true;
    this.otherOfficeTableIsLoading = true;

    const facility: FacilityGetModel = await this.getFacility();
    this.facility = facility;
    const notificationMaps: NotificationMaps = this.createNotificationMaps(facility.notifications);
    this.notificationMaps = notificationMaps;

    // 利用者グループテーブルの行を作成する
    const residentGroups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const residentGroupTableItems: Array<NotificationSettingTableItem> = this.createResidentGroupTableItems(residentGroups, notificationMaps);
    this.residentGroupTableItems = residentGroupTableItems;
    this.messageShowText = facility.notifications.messageShowText;
    this.residentGroupTableIsLoading = false;

    // 施設関係者テーブルの行を作成する
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherUserGroupTypes();
    const otherUserGroupTypeNameMap: Map<number, string> = this.createOtherUserGroupTypeNameMap(otherUserGroupTypes);
    const otherUserGroups: Array<OtherUserGroupModel> = await this.getOtherUserGroups();
    const otherUserGroupTableItems: Array<NotificationSettingTableItem> = this.createOtherUserGroupTableItems(otherUserGroups, otherUserGroupTypeNameMap, notificationMaps);
    this.otherUserGroupTableItems = otherUserGroupTableItems;
    this.otherUserMessageShowText = facility.notifications.otherUserMessageShowText;
    this.otherUserGroupTableIsLoading = false;

    // 他事業所連携テーブルの行を作成する
    const otherOfficeTableItems: Array<NotificationSettingTableItem> = this.createOtherOfficeTableItems(facility.notifications);
    this.otherOfficeTableItems = otherOfficeTableItems;
    this.otherOfficeMessageShowText = facility.notifications.otherOfficeMessageShowText;
    this.otherOfficeTableIsLoading = false;

    return;
  }

  /**
   * ### 施設取得
   */
  public async getFacility(): Promise<FacilityGetModel> {
    const response: FacilityGetModel = await this.facilitiesService.get(userStore);
    return response;
  }

  /**
   * ### 家族・施設関係者のLINEbot操作の通知設定をMAP化する
   *
   * - 以下は利用者グループIDをkey、通知対象メールアドレス配列をvalueとする
   *   - 家族、家族メッセージ、面会予約、イベント、写真印刷依頼
   * - 以下は施設関係者グループIDをkey、通知対象メールアドレス配列をvalueとする
   *   - 施設関係者、施設関係者メッセージ
   */
  public createNotificationMaps(notifications: Notifications): NotificationMaps {
    const eventMap: Map<number, string[]> = this.createNotificationCloumnMap(notifications.event);
    const familyMap: Map<number, string[]> = this.createNotificationCloumnMap(notifications.family);
    const messageMap: Map<number, string[]> = this.createNotificationCloumnMap(notifications.message);
    const otherUserMap: Map<number, string[]> = this.createNotificationCloumnMap(notifications.otherUser);
    const otherUserMessageMap: Map<number, string[]> = this.createNotificationCloumnMap(notifications.otherUserMessage);
    const pictureMap: Map<number, string[]> = this.createNotificationCloumnMap(notifications.picture);
    const reservationMap: Map<number, string[]> = this.createNotificationCloumnMap(notifications.reservation);
    const outingMap: Map<number, string[]> = this.createNotificationCloumnMap(notifications.outing);

    const notificationMaps: NotificationMaps = {
      // 家族ユーザー
      event: eventMap,
      family: familyMap,
      message: messageMap,
      picture: pictureMap,
      reservation: reservationMap,
      outing: outingMap,
      // 施設関係者グループ
      otherUser: otherUserMap,
      otherUserMessage: otherUserMessageMap
    };

    return notificationMaps;
  }

  /**
   * ### 通知設定のMAP化、MAP作成の実処理
   */
  public createNotificationCloumnMap(objects: Array<NotificationObject>): Map<number, string[]> {
    const map: Map<number, string[]> = new Map<number, string[]>();
    for (const object of objects) {
      map.set(object.groupId, object.mailAddress);
    }
    return map;
  }

  /**
   * ### 利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### テーブルの行を作成する
   *
   * - 家族メッセージのみ匿名があり得るので、匿名の行を追加する
   * - 利用者グループ単位で行の情報を作成する
   *   - 通知設定のMAPから情報を取得する
   *   - 存在すればそれを使用する
   *   - 存在しなければ空文字1件の配列を作成して使用する
   */
  public createResidentGroupTableItems(residentGroups: Array<ResidentGroupsGetModel>, notificationMaps: NotificationMaps): Array<NotificationSettingTableItem> {
    let items: Array<NotificationSettingTableItem> = [];

    /*
    #1874 匿名は一旦非表示にするが、施設単位で匿名を復活させる可能性があるため、コメントアウトしておく
    let anonymousMessageMailAddress: string[] | undefined = notificationMaps.message.get(0);
    const anonymousMessage: string[] = anonymousMessageMailAddress && 0 < anonymousMessageMailAddress.length ? anonymousMessageMailAddress : [""];
    items.push({
      groupId: 0,
      groupName: "匿名",
      event: [],
      family: [],
      message: anonymousMessage,
      otherUser: [],
      otherUserMessage: [],
      picture: [],
      reservation: []
    });
    */

    for (const residentGroup of residentGroups) {
      const eventMailAddress: string[] | undefined = notificationMaps.event.get(residentGroup.id);
      const event: string[] = eventMailAddress && 0 < eventMailAddress.length ? eventMailAddress : [""];

      const familyMailAddress: string[] | undefined = notificationMaps.family.get(residentGroup.id);
      const family: string[] = familyMailAddress && 0 < familyMailAddress.length ? familyMailAddress : [""];

      const messageMailAddress: string[] | undefined = notificationMaps.message.get(residentGroup.id);
      const message: string[] = messageMailAddress && 0 < messageMailAddress.length ? messageMailAddress : [""];

      const pictureMailAddress: string[] | undefined = notificationMaps.picture.get(residentGroup.id);
      const picture: string[] = pictureMailAddress && 0 < pictureMailAddress.length ? pictureMailAddress : [""];

      const reservationMailAddress: string[] | undefined = notificationMaps.reservation.get(residentGroup.id);
      const reservation: string[] = reservationMailAddress && 0 < reservationMailAddress.length ? reservationMailAddress : [""];

      const outingMailAddress: string[] | undefined = notificationMaps.outing.get(residentGroup.id);
      const outing: string[] = outingMailAddress && 0 < outingMailAddress.length ? outingMailAddress : [""];

      items.push({
        groupId: residentGroup.id,
        groupName: residentGroup.name,
        event: event,
        family: family,
        message: message,
        otherUser: [],
        otherUserMessage: [],
        picture: picture,
        reservation: reservation,
        outing: outing,
        otherOfficeMessage: [],
        otherOfficeOnlineMeeting: []
      });
    }

    return items;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherUserGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const response: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);
    return response;
  }

  /**
   * ### 施設関係者グループ種類IDと施設関係者グループ種類名のMAPを作成する
   */
  public createOtherUserGroupTypeNameMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    let typeMap: Map<number, string> = new Map<number, string>();
    for (const type of otherUserGroupTypes) {
      typeMap.set(type.id, type.name);
    }
    return typeMap;
  }

  /**
   * ### 施設関係者グループを取得する
   */
  public async getOtherUserGroups(): Promise<Array<OtherUserGroupModel>> {
    let response: Array<OtherUserGroupModel> = [];

    try {
      response = await this.otherUserGroupsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.otherUserGroupsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.otherUserGroupsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### テーブルの行を作成する
   *
   * - 施設関係者メッセージのみ匿名があり得るので、匿名の行を追加する
   * - 施設関係者グループ単位で行の情報を作成する
   *   - 通知設定のMAPから情報を取得する
   *   - 存在すればそれを使用する
   *   - 存在しなければ空文字1件の配列を作成して使用する
   */
  public createOtherUserGroupTableItems(
    otherUserGroups: Array<OtherUserGroupModel>,
    otherUserGroupTypeMap: Map<number, string>,
    notificationMaps: NotificationMaps
  ): Array<NotificationSettingTableItem> {
    let items: Array<NotificationSettingTableItem> = [];

    /*
    #1874 匿名は一旦非表示にするが、施設単位で匿名を復活させる可能性があるため、コメントアウトしておく
    const anonymousMessageMailAddress: string[] | undefined = notificationMaps.otherUserMessage.get(0);
    const anonymousMessage: string[] = anonymousMessageMailAddress && 0 < anonymousMessageMailAddress.length ? anonymousMessageMailAddress : [""];
    items.push({
      groupId: 0,
      groupName: "匿名",
      event: [],
      family: [],
      message: [],
      otherUser: [],
      otherUserMessage: anonymousMessage,
      picture: [],
      reservation: []
    });
    */

    for (const otherUserGroup of otherUserGroups) {
      const type: string | undefined = otherUserGroupTypeMap.get(otherUserGroup.id);
      const typeName: string = type ? `（${type}）` : "";

      const otherUserMailAddress: string[] | undefined = notificationMaps.otherUser.get(otherUserGroup.id);
      const otherUser: string[] = otherUserMailAddress && 0 < otherUserMailAddress.length ? otherUserMailAddress : [""];

      const otherUserMessageMailAddress: string[] | undefined = notificationMaps.otherUserMessage.get(otherUserGroup.id);
      const otherUserMessage: string[] = otherUserMessageMailAddress && 0 < otherUserMessageMailAddress.length ? otherUserMessageMailAddress : [""];

      items.push({
        groupId: otherUserGroup.id,
        groupName: `${otherUserGroup.name}${typeName}`,
        event: [],
        family: [],
        message: [],
        otherUser: otherUser,
        otherUserMessage: otherUserMessage,
        picture: [],
        reservation: [],
        outing: [],
        otherOfficeMessage: [],
        otherOfficeOnlineMeeting: []
      });
    }

    return items;
  }

  /**
   * ### テーブルの行を作成する
   *
   * - 家族メッセージのみ匿名があり得るので、匿名の行を追加する
   * - 利用者グループ単位で行の情報を作成する
   *   - 通知設定のMAPから情報を取得する
   *   - 存在すればそれを使用する
   *   - 存在しなければ空文字1件の配列を作成して使用する
   */
  public createOtherOfficeTableItems(notifications: Notifications): Array<NotificationSettingTableItem> {
    const items: Array<NotificationSettingTableItem> = [];

    const otherOfficeMessage = notifications.otherOfficeMessage;
    const otherOfficeMessageMailAddress: string[] = otherOfficeMessage && 0 < otherOfficeMessage.length ? otherOfficeMessage[0].mailAddress : [""];

    const otherOfficeOnlineMeeting = notifications.otherOfficeOnlineMeeting;
    const otherOfficeOnlineMeetingMailAddress: string[] = otherOfficeOnlineMeeting && 0 < otherOfficeOnlineMeeting.length ? otherOfficeOnlineMeeting[0].mailAddress : [""];
    items.push({
      groupId: 0,
      groupName: "",
      event: [],
      family: [],
      message: [],
      otherUser: [],
      otherUserMessage: [],
      picture: [],
      reservation: [],
      outing: [],
      otherOfficeMessage: otherOfficeMessageMailAddress,
      otherOfficeOnlineMeeting: otherOfficeOnlineMeetingMailAddress
    });

    return items;
  }

  /**
   * ### 更新確認モーダルを表示する
   */
  public async onUpdateButton(type: NotificationTabType): Promise<void> {
    this.showUpdateModal = true;
    this.notificationTabType = type;
    return;
  }

  /**
   * ### 通知設定を更新する
   */
  public async updateNotificationSettings(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const requestBody: NotificationSettingsPatchModel | null = this.createRequestBody(
        this.residentGroupTableItems,
        this.otherUserGroupTableItems,
        this.otherOfficeTableItems,
        this.messageShowText,
        this.otherUserMessageShowText,
        this.otherOfficeMessageShowText,
        this.facility as FacilityGetModel
      );
      if (requestBody) {
        await this.notificationsService.patchNotificationSettings(userStore, requestBody);
      }

      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        const message: string = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
        switch (this.notificationTabType) {
          case NotificationTabType.RESIDENT_GROUP: {
            this.residentGroupErrorMessage = message;
            break;
          }
          case NotificationTabType.OTHERUSER_GROUP: {
            this.otherUserGroupErrorMessage = message;
            break;
          }
          case NotificationTabType.OTHEROFFICE: {
            this.otherOfficeErrorMessage = message;
            break;
          }
        }
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### リクエストボディを作成する
   *
   * - 子コンポーネントで入力した各通知設定をリクエスト用に整形する
   * - 更新対象種別に応じてリクエストボディを作成する
   * - 利用者グループの場合
   *   - 利用者グループの入力した通知設定のみを使用してリクエストボディを作成する
   *   - 施設関係者グループの通知設定は既存のものを使用する
   * - 施設関係者グループの場合
   *   - 施設関係者グループの入力した通知設定のみを使用してリクエストボディを作成する
   *   - 利用者グループの通知設定は既存のものを使用する
   */
  public createRequestBody(
    residentGroupItems: Array<NotificationSettingTableItem>,
    otherUserGroupItems: Array<NotificationSettingTableItem>,
    otherOfficeTableItems: Array<NotificationSettingTableItem>,
    messageShowText: boolean,
    otherUserMessageShowText: boolean,
    otherOfficeMessageShowText: boolean,
    facility: FacilityGetModel
  ): NotificationSettingsPatchModel | null {
    switch (this.notificationTabType) {
      // 利用者グループの更新の場合
      case NotificationTabType.RESIDENT_GROUP: {
        // 家族ユーザー更新情報を作成
        const event: Array<NotificationObject> = this.createNotificationObjects(residentGroupItems, "event");
        const family: Array<NotificationObject> = this.createNotificationObjects(residentGroupItems, "family");
        const message: Array<NotificationObject> = this.createNotificationObjects(residentGroupItems, "message");
        const picture: Array<NotificationObject> = this.createNotificationObjects(residentGroupItems, "picture");
        const reservation: Array<NotificationObject> = this.createNotificationObjects(residentGroupItems, "reservation");
        const outing: Array<NotificationObject> = this.createNotificationObjects(residentGroupItems, "outing");

        const requestBody: NotificationSettingsPatchModel = {
          notifications: {
            // 家族ユーザーは更新対象
            event: event,
            family: family,
            message: message,
            messageShowText: messageShowText,
            picture: picture,
            reservation: reservation,
            outing: outing,
            // 施設関係者グループは更新対象外
            otherUser: facility.notifications.otherUser,
            otherUserMessage: facility.notifications.otherUserMessage,
            otherUserMessageShowText: facility.notifications.otherUserMessageShowText,
            // 他事業所連携は更新対象外
            otherOfficeMessage: facility.notifications.otherOfficeMessage,
            otherOfficeMessageShowText: facility.notifications.otherOfficeMessageShowText,
            otherOfficeOnlineMeeting: facility.notifications.otherOfficeOnlineMeeting
          },
          updated: facility.updated
        };

        return requestBody;
      }
      // 施設関係者グループの場合
      case NotificationTabType.OTHERUSER_GROUP: {
        // 施設関係者グループ更新情報を作成
        const otherUser: Array<NotificationObject> = this.createNotificationObjects(otherUserGroupItems, "otherUser");
        const otherUserMessage: Array<NotificationObject> = this.createNotificationObjects(otherUserGroupItems, "otherUserMessage");

        const requestBody: NotificationSettingsPatchModel = {
          notifications: {
            // 家族ユーザーは更新対象外
            event: facility.notifications.event,
            family: facility.notifications.family,
            message: facility.notifications.message,
            messageShowText: facility.notifications.messageShowText,
            picture: facility.notifications.picture,
            reservation: facility.notifications.reservation,
            outing: facility.notifications.outing,
            // 施設関係者グループは更新対象
            otherUser: otherUser,
            otherUserMessage: otherUserMessage,
            otherUserMessageShowText: otherUserMessageShowText,
            // 他事業所連携は更新対象外
            otherOfficeMessage: facility.notifications.otherOfficeMessage,
            otherOfficeMessageShowText: facility.notifications.otherOfficeMessageShowText,
            otherOfficeOnlineMeeting: facility.notifications.otherOfficeOnlineMeeting
          },
          updated: facility.updated
        };

        return requestBody;
      }
      // 他事業所連携
      case NotificationTabType.OTHEROFFICE: {
        const otherOfficeMessage: Array<NotificationObject> = this.createNotificationObjects(otherOfficeTableItems, "otherOfficeMessage");
        const otherOfficeOnlineMeeting: Array<NotificationObject> = this.createNotificationObjects(otherOfficeTableItems, "otherOfficeOnlineMeeting");

        const requestBody: NotificationSettingsPatchModel = {
          notifications: {
            // 家族ユーザーは更新対象外
            event: facility.notifications.event,
            family: facility.notifications.family,
            message: facility.notifications.message,
            messageShowText: facility.notifications.messageShowText,
            picture: facility.notifications.picture,
            reservation: facility.notifications.reservation,
            outing: facility.notifications.outing,
            // 施設関係者グループは更新対象
            otherUser: facility.notifications.otherUser,
            otherUserMessage: facility.notifications.otherUserMessage,
            otherUserMessageShowText: facility.notifications.otherUserMessageShowText,
            // 他事業所連携は更新対象
            otherOfficeMessage: otherOfficeMessage,
            otherOfficeMessageShowText: otherOfficeMessageShowText,
            otherOfficeOnlineMeeting: otherOfficeOnlineMeeting
          },
          updated: facility.updated
        };

        return requestBody;
      }
    }
    return null;
  }

  /**
   * ### 画面入力情報から通知設定を作成する
   */
  public createNotificationObjects(items: Array<NotificationSettingTableItem>, propertyName: keyof NotificationSettingTableItem): Array<NotificationObject> {
    let objects: Array<NotificationObject> = [];

    for (const item of items) {
      let updateMailAddress: string[] = [];

      for (const mailAddress of item[propertyName] as string[]) {
        if (mailAddress != "" && mailAddress != undefined && mailAddress != null) {
          updateMailAddress.push(mailAddress);
        }
      }

      if (0 < updateMailAddress.length) {
        objects.push({
          groupId: item.groupId,
          mailAddress: updateMailAddress
        });
      }
    }

    return objects;
  }
}

interface NotificationMaps {
  /** イベント */
  event: Map<number, string[]>;
  /** 家族 */
  family: Map<number, string[]>;
  /** 家族メッセージ */
  message: Map<number, string[]>;
  /** 施設関係者 */
  otherUser: Map<number, string[]>;
  /** 施設関係者メッセージ */
  otherUserMessage: Map<number, string[]>;
  /** 写真印刷依頼 */
  picture: Map<number, string[]>;
  /** 面会予約 */
  reservation: Map<number, string[]>;
  /** 外出外泊届け */
  outing: Map<number, string[]>;
}

enum NotificationTabType {
  UNKNOWN = -1,
  RESIDENT_GROUP = 0,
  OTHERUSER_GROUP = 1,
  OTHEROFFICE = 2
}
</script>

<style lang="scss" scoped>
#notification-setting-edit {
  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }
  }
  // タブが:beforeで描画されるので横幅指定しないと子コンポーネントが画面をはみ出る
  .tabs {
    width: 100%;
  }
}
</style>
